import Mux from '@mux/mux-player-react';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';

import VideoIconOverlay from 'dpl/breeder_search_results/components/VideoIconOverlay';
import { isMobileUA } from 'dpl/shared/utils';

const IS_MOBILE = isMobileUA();
const UNSUPPORTED_PLAYBACK_ERRORS = ['NotAllowedError', 'NotSupportedError'];

const DEFAULT_MUX_CSS_VARIABLES = {
  '--bottom-controls': 'none',
  '--center-seek-forward-button': 'none',
  '--center-seek-backward-button': 'none',
  '--bottom-play-button': 'none',
  '--bottom-volume-range': 'none',
  '--bottom-time-range:': 'none',
  '--bottom-time-display': 'none',
  '--bottom-duration-display': 'none',
  '--bottom-mute-button': 'none',
  '--bottom-captions-button': 'none',
  '--bottom-airplay-button': 'none',
  '--bottom-pip-button': 'none',
  '--bottom-playback-rate-button': 'none'
};

const videoMediaFileDefaultPropVideoProps = {};
/** @param {any} props */
export default function VideoMediaFile({
  videoProps = videoMediaFileDefaultPropVideoProps,
  mediaFile,
  muxRef = null
}) {
  const ref = useRef();
  const videoRef = muxRef || ref;
  const [canPlay, setCanPlay] = useState(true);

  const playbackId = mediaFile.playback_ids?.[0]?.id;
  const { style, ...muxProps } = videoProps;
  const muxStyle = { ...DEFAULT_MUX_CSS_VARIABLES, ...style };

  async function play() {
    if (!canPlay || !videoRef.current) return;

    try {
      await videoRef.current.play();
    } catch (error) {
      if (UNSUPPORTED_PLAYBACK_ERRORS.includes(error.name)) {
        setCanPlay(false);
      }
    }
  }

  function pause() {
    if (!videoRef.current) return;
    videoRef.current.pause();
  }

  function handleMouseEnter() {
    if (IS_MOBILE) return;
    play();
  }

  function handleMouseLeave() {
    if (IS_MOBILE) return;
    pause();
  }

  function handleInViewChange(inView) {
    if (!IS_MOBILE) return;

    if (inView) {
      play();
    } else {
      pause();
    }
  }

  return (
    <InView onChange={handleInViewChange} threshold={0.75}>
      <div
        data-test-id={`card-media-carousel-video-${mediaFile.id}`}
        className="VideoMediaFile relative h-100 db relative"
        style={{ width: style.width }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Mux
          ref={videoRef}
          playbackId={playbackId}
          style={muxStyle}
          preload="none"
          {...muxProps}
        />
        {!IS_MOBILE && (
          <VideoIconOverlay
            className="absolute pointer-events-none z-1"
            containerClassName="pv2 ph2"
            style={{ left: '8px', bottom: '8px' }}
            height="16px"
            width="16px"
          />
        )}
      </div>
    </InView>
  );
}

VideoMediaFile.propTypes = {
  mediaFile: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    analytics: PropTypes.shape({
      view: PropTypes.string.isRequired,
      view_feature: PropTypes.string.isRequired,
      view_feature_location: PropTypes.string.isRequired
    }),
    url: PropTypes.string,
    alt: PropTypes.string,
    experiment_clickthrough_url: PropTypes.string,
    playback_ids: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        policy: PropTypes.string.isRequired
      })
    ),
    status: PropTypes.string,
    aspect_ratio: PropTypes.string,
    type: PropTypes.string,
    puppy_id: PropTypes.number
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types,
  videoProps: PropTypes.object,
  muxRef: PropTypes.shape({ current: PropTypes.node })
};
